import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS, GRADIENTS, AFFILIATE_FORM, AFFILIATE_FORM_ANIM, CASINO_SHEET, NAV_DESKTOP, MOBILE_MQ } from '../../../../theme/tokens';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
/** Rises into place from below the viewport (and leaves the same way), like the shared sheets. */
const slideIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

/* Full-viewport dim behind the preview panel (portalled to <body>, so it covers everything).
   Sits just BELOW the Sheet modal band (Sheet: scrim 1999, backdrop/dialog 2000) so an EditCreativeSheet
   opened by clicking a banner INSIDE the preview stacks on top of it (was 2000/2001 — the preview panel
   covered the edit sheet). Still well above all app chrome (≤ 1000). */
export const Backdrop = styled.div<{ $closing?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1997;
  background: ${AFFILIATE_FORM.backdrop};
  animation: ${(p) => (p.$closing ? fadeOut : fadeIn)} ${(p) => (p.$closing ? CASINO_SHEET.CLOSE_MS : CASINO_SHEET.OPEN_MS)}ms
    ${(p) => (p.$closing ? CASINO_SHEET.EXIT_EASE : AFFILIATE_FORM_ANIM.EASE)} both;
`;

/* The preview panel. Mobile: full screen. Desktop: pinned to the content card's LEFT + TOP (via the
   live --content-* vars MainLayout publishes), so it sits neatly inside the content area — aligned with
   the sidebar top and never pushed past the content — and extended DOWN to the viewport's bottom edge.
   Portalled to <body> so `position: fixed` is viewport-relative (a transformed layout ancestor would
   otherwise re-root it and push it off). Slides up on open, down on close. */
export const Panel = styled.div<{ $closing?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1998;
  display: flex;
  flex-direction: column;
  background: ${COLORS.surfaceCard};
  overflow: hidden;
  transform: ${(p) => (p.$closing ? 'translateY(100%)' : 'translateY(0)')};
  transition: ${(p) => (p.$closing ? `transform ${CASINO_SHEET.CLOSE_MS}ms ${CASINO_SHEET.EXIT_EASE}` : 'none')};
  animation: ${(p) =>
    p.$closing ? 'none' : css`${slideIn} ${CASINO_SHEET.OPEN_MS}ms ${AFFILIATE_FORM_ANIM.EASE} backwards`};

  ${NAV_DESKTOP} {
    top: var(--content-top, 0px);
    left: var(--content-left, 0px);
    width: var(--content-width, 100vw);
    right: auto;
    bottom: 0;
    border: 1px solid ${COLORS.hairline};
    border-bottom: none;
    border-radius: 16px 16px 0 0;
  }
`;

export const Header = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 18px;
  border-bottom: 1px solid ${AFFILIATE_FORM.hairline};
`;

/* Header center slot (the device pill): absolutely centred on the full header width so it stays put
   regardless of the title / close-button widths. Desktop only. */
export const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;

  @media ${MOBILE_MQ} {
    display: none;
  }
`;

export const Title = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 17px;
  font-weight: 900;
  line-height: 1.15;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: none;
  border-radius: 99px;
  background: ${AFFILIATE_FORM.closeCircle};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/* Edge-to-edge preview body: no padding, the embedded site fills it corner to corner. */
export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
`;
